import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';
import { ConneqtRepository } from '../../state/reduxRepositories';
import { di } from '../../state/inversify.config';
import { ConneqtStatus } from '../../state/reduxRepositories/conneqt/conneqt.interfaces';
import { clearIcon, colouredWarning, notClearIcon } from '../../assets/icons';
import { getRouteByAccountType } from '../../routes/getRouteByAccountType';
import { getInitials } from '../../helpers/initialsExtractor';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { Avatar } from '../avatar/avatar';

interface PlayerCardProps {
  player: Player;
  onRemovePlayer?: any;
}

export function PlayerCard({ player, onRemovePlayer }: PlayerCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const conneqtRepository = di.get<ConneqtRepository>(ConneqtRepository);

  // TODO: move into a domainService and only return accountType or even better find a way so that you don't have to send down the accountType
  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const isConnected =
    conneqtRepository
      .useResourcesSelector()
      .filter(
        (conneqt) =>
          conneqt.status === ConneqtStatus.Conneqted &&
          (conneqt.requestPlayerId === player.id ||
            conneqt.responsePlayerId === player.id),
      ).length > 0;

  const navigateToPlayerProfile = () => {
    navigate(
      `/${getRouteByAccountType(
        loggedInUser.accountType,
      )}/player-profile?selectedPlayerId=${player?.id}`,
    );
  };

  return (
    <Card
      onClick={navigateToPlayerProfile}
      data-testid="openPlayerPage"
      sx={{ borderRadius: 4 }}
    >
      <CardHeader
        avatar={
          <Avatar
            title={getInitials(`${player.firstName} ${player.lastName}`)}
          />
        }
        sx={{
          '&:hover': {
            cursor: 'pointer',
          },
        }}
        action={
          <Grid>
            {player.clearedToPlay ? (
              <Grid
                item
                display="flex"
                alignItems="center"
                columnGap={1}
                sx={{ color: theme.palette.text.primary }}
              >
                {clearIcon} {t('CLEAR')}
              </Grid>
            ) : (
              <Grid
                item
                display="flex"
                alignItems="center"
                columnGap={1}
                sx={{ color: theme.palette.text.primary }}
              >
                {notClearIcon}
                <span
                  data-testid={`playerStatus-${player.firstName} ${player.lastName}`}
                >
                  {t('NOT_CLEAR')}
                </span>
              </Grid>
            )}
          </Grid>
        }
        title={`${player.firstName} ${player.lastName}`}
        titleTypographyProps={{ variant: 'h6' }}
        subheaderTypographyProps={{ variant: 'subtitle2' }}
        subheader={
          <Grid>
            {isConnected ? (
              <Grid
                item
                display="flex"
                alignItems="center"
                columnGap={1}
                sx={{ color: theme.palette.text.primary }}
              >
                {clearIcon} {t('CONNEQTED').toUpperCase()}
              </Grid>
            ) : (
              <Grid
                item
                display="flex"
                alignItems="center"
                columnGap={1}
                sx={{ color: theme.palette.text.primary }}
              >
                {colouredWarning} {t('NOT_CONNEQTED').toUpperCase()}
              </Grid>
            )}
          </Grid>
        }
      />
    </Card>
  );
}
